import { BabyVaccinationDetail } from '@hhgtech/hhg-components/types'
import dayjs from 'dayjs'
import { IntlFormatters } from 'react-intl'
import * as yup from 'yup'

import { OTHER_FIELD_KEY } from 'components/atoms/healthTools/form/additionalQuestionField'
import { BABY_GROWTH_MAX_AGE, ENV } from 'interfaces/constants'
import { ERROR_MESSAGE } from 'interfaces/error-message'
import { Gender, LOCALE } from 'interfaces/types'
import { ageGenerate } from 'utils/ageGenerate'

yup.addMethod(
  yup.object,
  'atLeastOneOf',
  function (list: Array<string>, f: IntlFormatters['formatMessage']) {
    return this.test({
      name: 'atLeastOneOf',
      message: f({
        id:
          ENV.DEPLOY_LOCALE === LOCALE.Vietnam
            ? ERROR_MESSAGE.BABY_GROWTH.AT_LEAST_ONE_OF_VN
            : ERROR_MESSAGE.BABY_GROWTH.AT_LEAST_ONE_OF,
        defaultMessage: 'Must have at least one of these fields: ${keys}',
      }),
      exclusive: true,
      params: { keys: list.join(', ') },
      test: (value: any) =>
        value == null ||
        list.some((f: any) => value[f] != null && value[f] != 'undefined'),
    })
  },
)

yup.addMethod(
  yup.string,
  'valIncludesStr',
  function (key: string, f: IntlFormatters['formatMessage']) {
    return this.test({
      name: 'valIncludesStr',
      message: f({
        id: 'additionalQuestion.inputErr.otherAnswerRequire',
        defaultMessage: 'Please fill your other answer',
      }),
      exclusive: false,
      test: (value: any) => !value.includes(key),
    })
  },
)

yup.addMethod(
  yup.mixed,
  'requireBabyNameIfCheckForYourKid',
  function (f: IntlFormatters['formatMessage']) {
    return this.test({
      name: 'requireBabyNameIfCheckForYourKid',
      message: f({
        id: 'tools.inputErr.requireKidNames',
        defaultMessage: `Please fill your kid's answer`,
      }),
      exclusive: false,
      test: (value: any, ctx) => (ctx.parent.isMyself ? Boolean(value) : true),
    })
  },
)

export const generateSchema = (
  f: IntlFormatters['formatMessage'],
  additionalFields: string[] = [],
) => {
  const addtionalValidate: { [key in string]: any } = {}
  additionalFields.map((i) => {
    // @ts-ignore
    addtionalValidate[i] = yup.string().valIncludesStr(OTHER_FIELD_KEY, f)
  })
  return (
    yup
      .object()
      .shape({
        // @ts-ignore
        // name: yup.mixed().requireBabyNameIfCheckForYourKid(f),
        gender: yup.string().required(
          f({
            id: 'tools.inputErr.requireBabyGender',
            defaultMessage: 'Please fill your baby gender',
          }),
        ),
        birthday: yup
          .date()
          .required(
            f({
              id: ERROR_MESSAGE.BABY_GROWTH.BIRTHDAY_REQUIRED,
              defaultMessage: 'Birthday is required',
            }),
          )
          .test({
            name: 'ageIsLowerThanNineteen',
            test: (_birthday, ctx) => {
              const age = ageGenerate(
                _birthday || new Date(),
                ctx.parent.measurementDate,
              )
              if (age.year > BABY_GROWTH_MAX_AGE) {
                return ctx.createError({
                  message: f(
                    {
                      id: ERROR_MESSAGE.BABY_GROWTH.INVALID_AGE,
                      defaultMessage: '',
                    },
                    { name: ctx.parent.name },
                  ),
                })
              }
              return true
            },
          }),
        height: yup.string(),
        weight: yup.string(),
        head: yup.string(),
        measurementDate: yup
          .date()
          .required()
          .default(new Date())
          .max(
            dayjs().add(1, 'day'),
            f({
              id: ERROR_MESSAGE.BABY_GROWTH.INVALID_MEASUREMENT_DATE,
              defaultMessage: 'Measurement day is not valid.',
            }),
          )
          .test('measurementDateTest', '', (_measurementDate, ctx) => {
            if (dayjs(_measurementDate).isBefore(ctx.parent.birthday)) {
              // Lower than birthday
              return ctx.createError({
                message: f(
                  {
                    id:
                      ERROR_MESSAGE.BABY_GROWTH
                        .MEASUREMENT_DATE_LOWER_THAN_BIRTHDAY,
                    defaultMessage:
                      'The measurement date must be less than {name} ’s birthday.',
                  },
                  {
                    name:
                      ctx.parent.name ||
                      f({
                        id: 'babyGrowth.emptyName',
                        defaultMessage: 'your baby',
                      }),
                  },
                ),
              })
            }
            return true
          }),
        ...addtionalValidate,
      })
      // @ts-ignore
      .atLeastOneOf(['height', 'weight', 'head'], f)
  )
}

export const generateGender = (
  gender: BabyVaccinationDetail['profile']['gender'],
) => (gender === 'male' ? Gender.Boy : Gender.Girl)
